import { type MaybeRef, breakpointsTailwind, useStorage } from '@vueuse/core'

export type CSSSizeString = `${number}px` | `${number}%` | `${number}rem`
export interface ResizeOptions {
  min: CSSSizeString
  max: CSSSizeString
  onResize?: (value: number) => void
  onReachMin?: () => void
}

const getScreenSize = () => {
  return {
    width: window.innerWidth,
    height: window.innerHeight,
  }
}

export const parseValuesToPX = (value: CSSSizeString) => {
  if (value.endsWith('px'))
    return parseInt(value.replace('px', ''))
  if (value.endsWith('%')) {
    const percent = parseInt(value.replace('%', ''))
    return (percent / 100) * getScreenSize().width
  }
  if (value.endsWith('em')) {
    const em = parseInt(value.replace('rem', ''))
    return em * 16
  }
  throw new Error('Invalid min value')
}

export function useResize(options: MaybeRef<ResizeOptions>, key: string) {
  const { width: windowWidth } = useWindowSize()

  const { isSmallerOrEqual } = useBreakpoints(breakpointsTailwind)
  const isResizing = ref(false)

  const div = ref<HTMLDivElement | null>(null)
  const current = useStorage<`${number}px`>(key, () => `${parseValuesToPX(unref(options).min)}px`,
  )

  onMounted(() => {
    if (isSmallerOrEqual('md'))
      current.value = `${parseValuesToPX('100%')}px`
  })

  function bindStyleEvents(element: HTMLDivElement) {
    const oldBorder = element.style.borderLeftColor
    element.addEventListener('mouseenter', () => {
    })

    element.addEventListener('mouseleave', () => {
      element.style.boxShadow = oldBorder
    })
  }

  const onDragStart = (e: MouseEvent) => {
    isResizing.value = true
    bindStyleEvents(div.value as HTMLDivElement)

    const option = unref(options)
    const min = parseValuesToPX(option.min)
    const pageX = e.pageX
    const parent = div.value?.parentElement as HTMLDivElement
    if (!parent)
      return
    const curColWidth = parent!.getClientRects()[0].width ?? min
    e.preventDefault()
    const onMouseMove = (e: MouseEvent) => {
      const newValue = Math.min(Math.max(((e.pageX - pageX) * -1) + curColWidth, min), parseValuesToPX(option.max))
      current.value = `${newValue}px`
    }
    const onMouseUp = () => {
      window.removeEventListener('mousemove', onMouseMove)
      window.removeEventListener('mouseup', onMouseUp)

      const rects = parent?.getBoundingClientRect()
      const itsOutOfScreen = rects ? (parseValuesToPX(unref(options).min) + rects.left) > windowWidth.value : false

      if (itsOutOfScreen) {
        const newValOutOfScreen = windowWidth.value - rects!.left
        if (newValOutOfScreen < parseValuesToPX(unref(options).min)) {
          current.value = `${parseValuesToPX(unref(options).min)}px`
          unref(options).onReachMin?.()
          return
        }
        else {
          current.value = `${newValOutOfScreen}px`
          return
        }
      }

      isResizing.value = false
    }

    window.addEventListener('mousemove', onMouseMove)
    window.addEventListener('mouseup', onMouseUp)
  }

  watch(div, (el) => {
    if (!el || !el.parentElement)
      return
    el.addEventListener('mousedown', onDragStart)
  })

  watch(windowWidth, (winWidth) => {
    if (isSmallerOrEqual('md')) {
      current.value = `${parseValuesToPX('100%')}px`
      return
    }

    const rects = div.value?.parentElement?.getBoundingClientRect()
    const itsOutOfScreen = rects ? (parseValuesToPX(unref(options).min) + rects.left) > winWidth : false

    if (itsOutOfScreen) {
      const newVal = winWidth - rects!.left
      if (newVal < parseValuesToPX(unref(options).min)) {
        current.value = `${parseValuesToPX(unref(options).min)}px`
        unref(options).onReachMin?.()
        return
      }
      current.value = `${newVal}px`
    }
  })

  watch(current, (value) => {
    const option = unref(options)
    if (option.onResize && value)
      option.onResize(parseValuesToPX(value as `${number}px`))
  })

  return {
    elementRef: div,
    current,
    isResizing,
  }
}
